import { useAppDispatch, useAppSelector } from '@/store'
import {
  setCurrentStep,
  setFormData,
  setValidation,
  resetRewardsData,
  setLoading,
  setError,
} from '@/store/slices/rewardsSlice'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {useNavigate, useParams} from 'react-router-dom'
import {ROUTES} from '@/utils/constants/routesConstants'
import {useDropdown} from '@/hooks/Dropdown-hook'
import {DROPDOWN_TYPES} from '@/utils/constants/dropdownConstants'
import { publishReward, getSegmentsList, getRewardDetail, saveRewardDraft} from '@/services/rewards'
import { REWARD_TYPES, isInventoryManagedRewardType } from '@/utils/constants/rewardsConstants'
import {useMemo, useCallback, useEffect} from 'react'
import { useQueries } from '@tanstack/react-query'
import { useDataTable } from './useDataTable-hook'
import { useRewardsDisplay } from '@/hooks/useRewardsDisplay'
import {
  useNameValidation,
  usePointsValidation,
  useOrderQuantityValidation,
  useBonusProductLimitValidation,
  validateFetchedProductsDetails,
  useRedeemPointsValidation,
  useRewardCashAmountValidation,
  useRewardCardTypeValidation,
  useValueValidation,
  useDigitalValueValidation,
  useDigitalPointsValidation,
  useDigitalSupplierValidation,
  useQuotaValidation,
  useMaxQuantityPerOrderValidation,
  useLoyaltyURLValidation,
} from '@/containers/Rewards/validations'
import { validateStartDate, validateEndDate } from '@/containers/Rewards/RewardAttributes/validations'
import { COUNTRY_LANGUAGES_CODES } from '@/utils/constants/globalConstants'

/**
 * Custom hook for rewards functionality
 * Provides mock data, step navigation, and form management
 */
export const useRewards = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { id } = useParams()
  const {currentStep, formData, validation, isLoading} = useAppSelector(state => state.rewards)
  const { setPageIndex, setSortingObj, setSearchArry, pageIndex, sortingObj, searchArr } = useDataTable()
  const countryCode = useAppSelector(state => state.countryCode)?.countryCode
  const displayContentLanguages = useAppSelector(state => state.common.languages)

  // Fetch reward types from API
  const {dropdownList: rewardTypes, isLoading: rewardTypesLoading} = useDropdown({
    dropdownType: DROPDOWN_TYPES.RewardTypes,
  })

  // Fetch segments from API
  const {dropdownList: segments, isLoading: segmentsLoading} = useDropdown({
    dropdownType: DROPDOWN_TYPES.RewardSegments,
  })

  // Fetch bonus products from API
  const {dropdownList: bonusProducts, isLoading: bonusProductsLoading} = useDropdown({
    dropdownType: DROPDOWN_TYPES.BonusProducts,
  })

  // Fetch visa cards rewardCardTypes from API
  const {dropdownList: rewardCardTypes, isLoading: rewardCardTypesLoading} = useDropdown({
    dropdownType: DROPDOWN_TYPES.RewardCardTypes,
  })

  // Validation hooks using centralized validations
  const {onChange: validateName} = useNameValidation()
  const {onChange: validatePoints} = usePointsValidation()
  const {onChange: validateOrderQuantity} = useOrderQuantityValidation()
  const {onChange: validateBonusProductLimit} = useBonusProductLimitValidation()
  const {onChange: validateReedemPoints} = useRedeemPointsValidation()
  const {onChange: validateRewardCashAmount} = useRewardCashAmountValidation()
  const {onChange: validateRewardCardType} = useRewardCardTypeValidation()
  const {onChange: validateValue} = useValueValidation()
  const {onChange: validateDigitalValue} = useDigitalValueValidation()
  const {onChange: validateDigitalPoints} = useDigitalPointsValidation()
  const {onChange: validateDigitalSupplier} = useDigitalSupplierValidation()
  const {onChange: validateQuota} = useQuotaValidation()
  const {onChange: validateMaxQuantityPerOrder} = useMaxQuantityPerOrderValidation()
  const {onChange: validateLoyaltyURL} = useLoyaltyURLValidation()

  const queryParams = useMemo(() => {
    const params = new URLSearchParams()

    // Add sorting parameters if available
    if (sortingObj?.column) {
      params.append('sortBy', sortingObj.column)
      params.append('order', sortingObj.direction)
    }

    // Add pagination parameters
    params.append('pageSize', 10)
    params.append('page', pageIndex || 0)


    return params.toString()
  }, [sortingObj?.column, sortingObj?.direction, pageIndex])
  // Fetch segments data using React Query
  const [{ data: segmentsData, isLoading: segmentsApiLoading }] = useQueries({
    queries: [
      {
        queryKey: ['SEGMENTS_LIST', queryParams, searchArr],
        queryFn: () => getSegmentsList(queryParams,searchArr),
      },
    ],
  })

  const loadDraft = useCallback(async (rewardIdToLoad) => {
    try {
      if (!rewardIdToLoad) return
      dispatch(setLoading(true))
      const resp = await getRewardDetail(rewardIdToLoad)
      const data = resp?.data || resp
      const apiRewardType = data?.rewardType || ''
      dispatch(setFormData({
        name: data?.name || '',
        rewardId: String(data?.id || data?.rewardId || rewardIdToLoad),
        rewardType: apiRewardType,
        digitalValue: data?.digitalCodeAmount ?? '',
        digitalSupplier: data?.digitalCodePartner ?? '',
        digitalPoints: data?.redeemPoints ?? '',
      }))
    } catch (e) {
      dispatch(setError(e?.message || 'Failed to load draft'))
    } finally {
      dispatch(setLoading(false))
    }
  }, [dispatch])

  // Initialize form when arriving via /rewards/create/:id
  // Run only once per route id: skip if rewardId already present in formData
  useEffect(() => {
    if (!id || formData?.rewardId) return

    const initFromRoute = async () => {
      await loadDraft(id)
      dispatch(setCurrentStep(4))
    }

    initFromRoute()
  }, [id, loadDraft, dispatch, formData?.rewardId])

  // Handle form field changes
  const handleFormChange = useCallback((field, value) => {
    dispatch(setFormData({[field]: value}))
  }, [dispatch])

  // Handle fetched products update
  const handleFetchedProductsUpdate = useCallback((products) => {
    dispatch(setFormData({fetchedProducts: products}))
  }, [dispatch])

  // Handle validation
  const handleValidation = (field, isValid) => {
    dispatch(setValidation({[`${field}Valid`]: isValid}))
  }

  // Helper function to validate bonus products reward type
  const validateBonusProductsType = (nameError, rewardTypeValid) => {
    const orderQuantityError = validateOrderQuantity(formData.orderQuantity)
    const bonusProductLimitError = validateBonusProductLimit(formData.bonusProductLimit)
    // Handle both array of objects and array of IDs for backward compatibility
    const selectedProductsValid = formData.selectedProducts &&
      Array.isArray(formData.selectedProducts) &&
      formData.selectedProducts.length > 0

    // Validate fetched products details
    const fetchedProductsDetailsValid = validateFetchedProductsDetails(formData.fetchedProducts)

    handleValidation('orderQuantity', !!orderQuantityError)
    handleValidation('bonusProductLimit', !!bonusProductLimitError)
    handleValidation('selectedProducts', !selectedProductsValid)

    return !nameError && !orderQuantityError && !bonusProductLimitError && selectedProductsValid && fetchedProductsDetailsValid && rewardTypeValid
  }

  // Helper function to validate visa card reward type
  const validateVisaCardType = (nameError, rewardTypeValid) => {
    const redeemPointsError = validateReedemPoints(formData.redeemPoints)
    const rewardCashAmountError = validateRewardCashAmount(formData.rewardCashAmount)
    const rewardCardTypeError = validateRewardCardType(formData.rewardCardType)
    handleValidation('redeemPoints', !!redeemPointsError)
    handleValidation('rewardCashAmount', !!rewardCashAmountError)
    handleValidation('rewardCardType', !!rewardCardTypeError)
    return !nameError && !redeemPointsError && !rewardCashAmountError && !rewardCardTypeError && rewardTypeValid
  }

  // Helper function to validate PEP voucher/product reward type
  const validatePepVoucherProductType = (nameError, rewardTypeValid) => {
    const pointsError = validatePoints(formData.points)
    // PRODUCT type uses selectedProducts, PEP_VOUCHER_PRODUCT uses selectedPepVouchersProducts
    const isProductType = formData.rewardType === REWARD_TYPES.PRODUCT
    const selectedProducts = isProductType
      ? formData.selectedProducts
      : formData.selectedPepVouchersProducts
    const validationKey = isProductType ? 'selectedProducts' : 'selectedPepVouchersProducts'

    const selectedProductsValid = selectedProducts &&
      Array.isArray(selectedProducts) &&
      selectedProducts.length > 0

    handleValidation('points', !!pointsError)
    handleValidation(validationKey, !selectedProductsValid)
    return !nameError && !pointsError && selectedProductsValid && rewardTypeValid
  }

  // Helper function to validate cash/discount/PEP voucher discount reward type
  const validateCashDiscountType = (nameError, rewardTypeValid) => {
    const pointsError = validatePoints(formData.points)
    const valueError = validateValue(formData.value)
    handleValidation('points', !!pointsError)
    handleValidation('value', !!valueError)
    return !nameError && !pointsError && !valueError && rewardTypeValid
  }

  // Helper function to validate digital code reward type
  const validateDigitalCodeType = (nameError, rewardTypeValid) => {
    const digitalValueError = validateDigitalValue(formData.digitalValue)
    const digitalPointsError = validateDigitalPoints(formData.digitalPoints)
    const digitalSupplierError = validateDigitalSupplier(formData.digitalSupplier)
    handleValidation('digitalValue', !!digitalValueError)
    handleValidation('digitalPoints', !!digitalPointsError)
    handleValidation('digitalSupplier', !!digitalSupplierError)
    return !nameError && !digitalValueError && !digitalPointsError && !digitalSupplierError && rewardTypeValid
  }

  // Helper function to validate loyalty reward type
  const validateLoyaltyType = (nameError, rewardTypeValid) => {
    const pointsError = validatePoints(formData.points)
    const loyaltyURLError = validateLoyaltyURL(formData.loyaltyURL)
    handleValidation('points', !!pointsError)
    handleValidation('loyaltyURL', !!loyaltyURLError)
    return !nameError && !pointsError && !loyaltyURLError && rewardTypeValid
  }

  // Helper function to validate default reward type
  const validateDefaultRewardType = (nameError, rewardTypeValid) => {
    const pointsError = validatePoints(formData.points)
    handleValidation('points', !!pointsError)
    return !nameError && !pointsError && rewardTypeValid
  }

  // Helper function to validate step 1 (setup step)
  const validateStep1 = () => {
    // Use the existing validation hooks for consistent logic
    const nameError = validateName(formData.name)
    const rewardTypeValid = formData.rewardType && formData.rewardType.length > 0

    // Update validation state
    handleValidation('name', !!nameError)
    handleValidation('rewardType', !rewardTypeValid)

    // Validate based on reward type
    if (formData.rewardType === REWARD_TYPES.BONUS_PRODUCTS) {
      return validateBonusProductsType(nameError, rewardTypeValid)
    } else if (formData.rewardType === REWARD_TYPES.VISA_CARD) {
      return validateVisaCardType(nameError, rewardTypeValid)
    } else if (formData.rewardType === REWARD_TYPES.PEP_VOUCHER_PRODUCT || formData.rewardType === REWARD_TYPES.PRODUCT) {
      return validatePepVoucherProductType(nameError, rewardTypeValid)
    } else if (formData.rewardType === REWARD_TYPES.CASH || formData.rewardType === REWARD_TYPES.DISCOUNT || formData.rewardType === REWARD_TYPES.PEP_VOUCHER_DISCOUNT) {
      return validateCashDiscountType(nameError, rewardTypeValid)
    } else if (formData.rewardType === REWARD_TYPES.DIGITAL || formData.rewardType === REWARD_TYPES.DIGITAL_PRE_ORDER) {
      return validateDigitalCodeType(nameError, rewardTypeValid)
    } else if (formData.rewardType === REWARD_TYPES.LOYALTY) {
      return validateLoyaltyType(nameError, rewardTypeValid)
    } else {
      return validateDefaultRewardType(nameError, rewardTypeValid)
    }
  }

  // Helper function to validate image and reward text for single image upload reward types in step 2
  // Applies to: VISA_CARD, CASH, PHYSICAL, DISCOUNT, PEP_VOUCHER_PRODUCT, DIGITAL, DIGITAL_PRE_ORDER, LOYALTY, PEP_VOUCHER_DISCOUNT
  const validateImageAndRewardTextStep2 = () => {
    // Get languages from country code or displayContentLanguages
    const countryLangs = COUNTRY_LANGUAGES_CODES[countryCode] || []
    const languages = (displayContentLanguages && displayContentLanguages.length > 0)
      ? displayContentLanguages
      : countryLangs

    // Validate image is present
    const imageUrl = formData?.imageUrls?.[0]
    const hasImage = typeof imageUrl === 'string' && imageUrl.trim().length > 0

    // Validate all languages have both title and description
    const rewardText = formData?.rewardText || {}
    const allLanguagesValid = languages.every(lang => {
      const code = lang.code || lang
      const text = rewardText[code] || {}
      const hasTitle = typeof text.title === 'string' && text.title.trim().length > 0
      const hasDescription = typeof text.description === 'string' && text.description.trim().length > 0
      return hasTitle && hasDescription
    })

    return hasImage && allLanguagesValid
  }

  // Helper function to validate step 2 (display step)
  const validateStep2 = () => {
    // Check if reward type uses single image upload and global reward text
    switch (formData.rewardType) {
      case REWARD_TYPES.VISA_CARD:
      case REWARD_TYPES.CASH:
      case REWARD_TYPES.PHYSICAL:
      case REWARD_TYPES.DISCOUNT:
      case REWARD_TYPES.PEP_VOUCHER_PRODUCT:
      case REWARD_TYPES.DIGITAL:
      case REWARD_TYPES.DIGITAL_PRE_ORDER:
      case REWARD_TYPES.LOYALTY:
      case REWARD_TYPES.PEP_VOUCHER_DISCOUNT:
        return validateImageAndRewardTextStep2()
      default: {
        // Display step validation: enable Next only if all selected products are fully completed
        // Completion now requires image/default image AND title/description for all languages
        const ids = (formData?.selectedProducts || []).map(p => p.value)
        if (ids.length === 0) return true
        return (displayApi?.completedCount?.count || 0) === ids.length
      }
    }
  }

  // Shared helper for step 3 validation (pure: no dispatches)
  const getStep3Errors = () => {
    const startDateError = validateStartDate(formData.startDateDisplay, formData.endDateDisplay)
    const endDateError = validateEndDate(formData.endDateDisplay, formData.startDateDisplay)

    const inventoryRewardType = isInventoryManagedRewardType(formData?.rewardType, formData?.rewardCardType)

    const shouldValidateQuota = inventoryRewardType && !formData.unlimitedInventory
    const quotaError = shouldValidateQuota ? validateQuota(formData.quota) : undefined

    const shouldValidateMaxQuantityPerOrder = formData.rewardType === REWARD_TYPES.PEP_VOUCHER_DISCOUNT
    const maxQuantityPerOrderError = shouldValidateMaxQuantityPerOrder
      ? validateMaxQuantityPerOrder(formData.maxQuantityPerOrder)
      : undefined

    return {
      startDateError,
      endDateError,
      quotaError,
      maxQuantityPerOrderError,
      shouldValidateQuota,
      shouldValidateMaxQuantityPerOrder,
    }
  }

  // Helper function to validate step 3 (attributes step)
  const validateStep3 = () => {
    const {
      startDateError,
      endDateError,
      quotaError,
      maxQuantityPerOrderError,
      shouldValidateQuota,
      shouldValidateMaxQuantityPerOrder,
    } = getStep3Errors()

    // Update validation state
    handleValidation('startDate', !!startDateError)
    handleValidation('endDate', !!endDateError)
    if (shouldValidateQuota) {
      handleValidation('quota', !!quotaError)
    }
    if (shouldValidateMaxQuantityPerOrder) {
      handleValidation('maxQuantityPerOrder', !!maxQuantityPerOrderError)
    }

    // Step is valid only if there are no date or required inventory errors
    return !startDateError && !endDateError && !quotaError && !maxQuantityPerOrderError
  }

  // Unified validation function using the existing validation hook
  // NOTE: This code is for development purpose only and we will remove once the entire flow is completed for all the steps
  const validateCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return validateStep1()
      case 2:
        return validateStep2()
      case 3:
        return validateStep3()
      case 4:
        return true
      default:
        return true
    }
  }

  // Helper function to check errors for bonus products reward type
  const hasBonusProductsErrors = (nameError, rewardTypeValid) => {
    const orderQuantityError = validateOrderQuantity(formData.orderQuantity)
    const bonusProductLimitError = validateBonusProductLimit(formData.bonusProductLimit)
    // Handle both array of objects and array of IDs for backward compatibility
    const selectedProductsValid = formData.selectedProducts &&
      Array.isArray(formData.selectedProducts) &&
      formData.selectedProducts.length > 0

    // Validate fetched products details
    const fetchedProductsDetailsValid = validateFetchedProductsDetails(formData.fetchedProducts)

    return !!nameError || !!orderQuantityError || !!bonusProductLimitError || !selectedProductsValid || !fetchedProductsDetailsValid || !rewardTypeValid
  }

  // Helper function to check errors for visa card reward type
  const hasVisaCardErrors = (nameError, rewardTypeValid) => {
    const redeemPointsError = validateReedemPoints(formData.redeemPoints)
    const rewardCashAmountError = validateRewardCashAmount(formData.rewardCashAmount)
    const rewardCardTypeError = validateRewardCardType(formData.rewardCardType)
    return !!nameError || !!redeemPointsError || !!rewardCashAmountError || !!rewardCardTypeError || !rewardTypeValid
  }

  // Helper function to check errors for PEP voucher/product reward type
  const hasPepVoucherProductErrors = (nameError, rewardTypeValid) => {
    // PRODUCT type uses selectedProducts, PEP_VOUCHER_PRODUCT uses selectedPepVouchersProducts
    const isProductType = formData.rewardType === REWARD_TYPES.PRODUCT
    const selectedProducts = isProductType
      ? formData.selectedProducts
      : formData.selectedPepVouchersProducts

    const selectedProductsValid = selectedProducts &&
      Array.isArray(selectedProducts) &&
      selectedProducts.length > 0
    const pointsError = validatePoints(formData.points)
    return !!nameError || !!pointsError || !selectedProductsValid || !rewardTypeValid
  }

  // Helper function to check errors for cash/discount/PEP voucher discount reward type
  const hasCashDiscountErrors = (nameError, rewardTypeValid) => {
    const pointsError = validatePoints(formData.points)
    const valueError = validateValue(formData.value)
    return !!nameError || !!pointsError || !!valueError || !rewardTypeValid
  }

  // Helper function to check errors for digital reward type
  const hasDigitalErrors = (nameError, rewardTypeValid) => {
    const digitalValueError = validateDigitalValue(formData.digitalValue)
    const digitalPointsError = validateDigitalPoints(formData.digitalPoints)
    const digitalSupplierError = validateDigitalSupplier(formData.digitalSupplier)
    return !!nameError || !!digitalValueError || !!digitalPointsError || !!digitalSupplierError || !rewardTypeValid
  }

  // Helper function to check errors for loyalty reward type
  const hasLoyaltyErrors = (nameError, rewardTypeValid) => {
    const pointsError = validatePoints(formData.points)
    const loyaltyURLError = validateLoyaltyURL(formData.loyaltyURL)
    return !!nameError || !!pointsError || !!loyaltyURLError || !rewardTypeValid
  }

  // Helper function to check errors for default reward type
  const hasDefaultRewardErrors = (nameError, rewardTypeValid) => {
    const pointsError = validatePoints(formData.points)
    return !!nameError || !!pointsError || !rewardTypeValid
  }

  // Helper function to check errors for step 1 (setup step)
  const hasStep1Errors = () => {
    const nameError = validateName(formData.name)
    const rewardTypeValid = formData.rewardType && formData.rewardType.length > 0

    if (formData.rewardType === REWARD_TYPES.BONUS_PRODUCTS) {
      return hasBonusProductsErrors(nameError, rewardTypeValid)
    } else if (formData.rewardType === REWARD_TYPES.VISA_CARD) {
      return hasVisaCardErrors(nameError, rewardTypeValid)
    } else if (formData.rewardType === REWARD_TYPES.PEP_VOUCHER_PRODUCT || formData.rewardType === REWARD_TYPES.PRODUCT) {
      return hasPepVoucherProductErrors(nameError, rewardTypeValid)
    } else if (formData.rewardType === REWARD_TYPES.CASH || formData.rewardType === REWARD_TYPES.DISCOUNT || formData.rewardType === REWARD_TYPES.PEP_VOUCHER_DISCOUNT) {
      return hasCashDiscountErrors(nameError, rewardTypeValid)
    } else if (formData.rewardType === REWARD_TYPES.DIGITAL || formData.rewardType === REWARD_TYPES.DIGITAL_PRE_ORDER) {
      return hasDigitalErrors(nameError, rewardTypeValid)
    } else if (formData.rewardType === REWARD_TYPES.LOYALTY) {
      return hasLoyaltyErrors(nameError, rewardTypeValid)
    } else {
      return hasDefaultRewardErrors(nameError, rewardTypeValid)
    }
  }


  // Helper function to check errors for step 2 (display step)
  const hasStep2Errors = () => {
    // Check if reward type uses single image upload and global reward text
    switch (formData.rewardType) {
      case REWARD_TYPES.VISA_CARD:
      case REWARD_TYPES.CASH:
      case REWARD_TYPES.PHYSICAL:
      case REWARD_TYPES.DISCOUNT:
      case REWARD_TYPES.PEP_VOUCHER_PRODUCT:
      case REWARD_TYPES.DIGITAL:
      case REWARD_TYPES.DIGITAL_PRE_ORDER:
      case REWARD_TYPES.LOYALTY:
      case REWARD_TYPES.PEP_VOUCHER_DISCOUNT:
        return !validateImageAndRewardTextStep2()
      default: {
        const ids = (formData?.selectedProducts || []).map(p => p.value)
        if (ids.length === 0) return false
        // Error state if not all completed per the stricter criteria
        return (displayApi?.completedCount?.count || 0) !== ids.length
      }
    }
  }

  // Helper function to check errors for step 3 (attributes step)
  const hasStep3Errors = () => {
    const {
      startDateError,
      endDateError,
      quotaError,
      maxQuantityPerOrderError,
    } = getStep3Errors()

    return !!startDateError || !!endDateError || !!quotaError || !!maxQuantityPerOrderError
  }

  // Check if form has errors for current step (used in CreateReward.jsx)
  const hasFormErrors = () => {
    switch (currentStep) {
      case 1:
        return hasStep1Errors()
      case 2:
        return hasStep2Errors()
      case 3:
        return hasStep3Errors()
      case 4:
        return false
      default:
        return false
    }
  }

  // Handle next step
  const handleNext = () => {
    if (validateCurrentStep()) {
      if (currentStep < 4) {
        dispatch(setCurrentStep(currentStep + 1))
      } else {
        handlePublish()
      }
    }
  }

  // Handle previous step
  const handlePrevious = () => {
    if (currentStep > 1) {
      dispatch(setCurrentStep(currentStep - 1))
    }
  }

  // Reward type mapping (API sample shows title-cased values like "Cash")
  const mapRewardType = (rt) => {
    if (!rt) return ''
    if (rt === REWARD_TYPES.CASH) return 'Cash'
    return rt
  }

  // Map selected products -> rewardProducts
  // PRODUCT type uses selectedProducts, PEP_VOUCHER_PRODUCT uses selectedPepVouchersProducts
  const buildRewardProducts = () => {
    const isProductType = formData?.rewardType === REWARD_TYPES.PRODUCT
    const productsToMap = isProductType
      ? formData?.selectedProducts
      : (formData?.selectedPepVouchersProducts || formData?.selectedProducts)
    if (!Array.isArray(productsToMap)) return []
    return productsToMap.map(p => ({
      productId: String(p.value ?? ''),
      productName: String(p.label ?? ''),
    }))
  }

  const buildIncludedSegmentGroups = () => {
    const segmentsLookup = new Map((segments || []).map(s => [String(s.id ?? s.value), s.name || s.label]))
    if (!Array.isArray(formData?.selectedSegments)) return []
    return formData.selectedSegments
      .filter(g => Array.isArray(g) && g.length > 0)
      .map(groupIds => ({
        segments: groupIds.map(id => ({
          segmentId: String(id),
          segmentName: String(segmentsLookup.get(String(id)) || id),
        })),
      }))
  }

  const buildExcludedSegments = () => {
    if (!Array.isArray(formData?.excludedSegments)) return []
    return formData.excludedSegments.map(s => ({
      segmentId: String(s.value ?? s.id ?? ''),
      segmentName: String(s.label ?? s.name ?? s.value ?? s.id ?? ''),
    }))
  }

  const getImageUrls = () => {
    return Array.isArray(formData?.imageUrls) ? formData.imageUrls.filter(Boolean) : []
  }

  const getProductsForDisplay = () => {
    const isProductType = formData?.rewardType === REWARD_TYPES.PRODUCT
    return isProductType
      ? formData?.selectedProducts
      : (formData?.selectedPepVouchersProducts || formData?.selectedProducts)
  }

  const getRewardText = (firstProductId) => {
    const productDisplayText = formData?.display?.productDisplay?.[firstProductId]?.rewardText || {}
    if (formData?.rewardText && Object.keys(formData.rewardText).length > 0) {
      return formData.rewardText
    }
    return productDisplayText
  }

  const toIso = (d) => {
    try {
      if (!d) return undefined
      const date = typeof d === 'string' ? new Date(d) : d
      return isNaN(date?.getTime?.()) ? undefined : date.toISOString()
    } catch {
      return undefined
    }
  }

  const buildDraftPayload = () => {
    const rewardProducts = buildRewardProducts()
    const includedSegmentGroups = buildIncludedSegmentGroups()
    const excludedSegments = buildExcludedSegments()
    const imageUrls = getImageUrls()
    const productsForDisplay = getProductsForDisplay()
    const firstProductId = productsForDisplay?.[0]?.value
    const rewardText = getRewardText(firstProductId)

    const payload = {
      name: formData?.name || '',
      status: 'DRAFT',
      rewardType: mapRewardType(formData?.rewardType),
      redeemPoints: Number(formData?.digitalPoints ?? formData?.redeemPoints ?? formData?.points ?? 0) || 0,
      rewardProducts,
      orderQty: Number(formData?.orderQuantity ?? 0) || 0,
      limitOfProductQty: Number(formData?.bonusProductLimit ?? 0) || 0,
      allCustomers: Boolean(formData?.allCustomers),
      includedSegmentGroups,
      segmentExclude: Boolean(formData?.excludeSegments),
      excludedSegments,
      defaultImage: false,
      imageUrls,
      alertDisplayCount: Number(formData?.display?.productDisplay?.[firstProductId]?.alertDisplayCount ?? 1) || 1,
      rewardText,
      startDate: toIso(formData?.startDateDisplay || formData?.startDate),
      endDate: toIso(formData?.endDateDisplay || formData?.endDate),
      displayOutOfStock: Boolean(formData?.displayOutOfStock ?? false),
      // Optional stock/inventory fields if present in formData; otherwise omit-like defaults
      quota: formData?.quota,
      leftOverInventory: formData?.leftOverInventory,
      maxQuantityPerOrder: formData?.maxQuantityPerOrder,
      physicalLimitPerStore: formData?.physicalLimitPerStore,
      physicalQuota: formData?.physicalQuota,
      physicalLeftOverInventory: formData?.physicalLeftOverInventory,
      unlimitedInventory: formData?.unlimitedInventory,
      inStock: formData?.inStock,
      digitalCodeAmount: formData?.digitalValue,
      digitalCodePartner: formData?.digitalSupplier,
    }

    // Include id for update if present; backend should create when absent
    if (formData?.rewardId) {
      payload.id = isNaN(Number(formData.rewardId)) ? String(formData.rewardId) : Number(formData.rewardId)
    }

    return payload
  }

  // Handle draft saving
  const handleDraft = async () => {
    try {
      dispatch(setLoading(true))

      const payload = buildDraftPayload()
      const resp = await saveRewardDraft(payload)
      const respData = resp?.data || resp
      const returnedId = respData?.id || respData?.rewardId
      if (returnedId && String(returnedId) !== String(formData.rewardId || '')) {
        dispatch(setFormData({ rewardId: String(returnedId) }))
      }
      const effectiveId = returnedId || formData?.rewardId
      if (effectiveId) {
        await loadDraft(effectiveId)
      }
      dispatch(
        setToastNotifications({
          show: true,
          message: 'Reward saved as draft successfully',
          type: 'success',
        })
      )
    } catch (error) {
      dispatch(setError(error.message))
      dispatch(
        setToastNotifications({
          show: true,
          message: 'Failed to save draft',
          type: 'error',
        })
      )
    } finally {
      dispatch(setLoading(false))
    }
  }

  // Handle publishing
  const handlePublish = async () => {
    try {
      dispatch(setLoading(true))
      await publishReward(formData)
      dispatch(
        setToastNotifications({
          show: true,
          message: 'Reward published successfully',
          type: 'success',
        })
      )
      navigate(ROUTES.REWARD_CATALOG)
    } catch (error) {
      dispatch(setError(error.message))
      dispatch(
        setToastNotifications({
          show: true,
          message: 'Failed to publish reward',
          type: 'error',
        })
      )
    } finally {
      dispatch(setLoading(false))
    }
  }

  // Handle cancel
  const handleCancel = () => {
    dispatch(resetRewardsData())
    navigate(ROUTES.REWARD_CATALOG)
  }

  // Expose setter for directly setting a step from UI (e.g., ProgressIndicator clicks)
  const setStep = (step) => {
    dispatch(setCurrentStep(step))
  }

  // Reset reward conditions
  const resetRewardConditions = () => {
    dispatch(
      setFormData({
        rewardType: '',
        points: '',
        selectedProducts: [],
        fetchedProducts: [],
        orderQuantity: '',
        bonusProductLimit: '',
        rewardCardType: '',
        rewardCashAmount: '',
        redeemPoints: '',
        // Digital-specific fields
        digitalValue: '',
        digitalSupplier: '',
        digitalPoints: '',
        selectedPepVouchersProducts: [],
        value: '',
        // Loyalty-specific fields
        loyaltyURL: '',
        couponBasedLoyaltyReward: false,
      })
    )
    dispatch(
      setValidation({
        rewardTypeValid: undefined,
        pointsValid: undefined,
        selectedProductsValid: undefined,
        orderQuantityValid: undefined,
        bonusProductLimitValid: undefined,
        rewardCardTypeValid: undefined,
        rewardCashAmountValid: undefined,
        redeemPointsValid: undefined,
        selectedPepVouchersProductsValid: undefined,
        valueValid: undefined,
        // Digital-specific flags
        digitalValueValid: undefined,
        digitalPointsValid: undefined,
        digitalSupplierValid: undefined,
        // Loyalty-specific flags
        loyaltyURLValid: undefined,
      })
    )
  }

  // ================= Display step logic moved to dedicated hook =================
  const displayApi = useRewardsDisplay({
    dispatch,
    formData,
    handleFormChange,
    countryCode,
    displayContentLanguages,
    currentStep,
  })

  return {
    // State
    currentStep,
    formData,
    validation,
    isLoading,

    // Data
    rewardTypes,
    segments,
    bonusProducts,
    rewardCardTypes,
    rewardTypesLoading,
    segmentsLoading,
    bonusProductsLoading,
    rewardCardTypesLoading,
    pepVouchersOrProductsLoading: bonusProductsLoading, // Reuse bonusProductsLoading for PEP vouchers/products

    // Segments API data
    segmentsApiData: segmentsData?.data || [],
    segmentsTotal: segmentsData?.totalRecords || 0,
    segmentsApiLoading,
    setPageIndex,
    setSortingObj,
    setSearchArry,
    pageIndex,
    sortingObj,
    searchArr,

    // Validation functions
    validateName,
    validatePoints,
    validateOrderQuantity,
    validateBonusProductLimit,
    validateReedemPoints,
    validateRewardCashAmount,
    validateRewardCardType,
    validateValue,
    hasFormErrors,
    validateDigitalValue,
    validateDigitalPoints,
    validateDigitalSupplier,
    validateLoyaltyURL,

    // Actions
    handleFormChange,
    handleFetchedProductsUpdate,
    handleValidation,
    handleNext,
    handlePrevious,
    handleDraft,
    handlePublish,
    handleCancel,
    resetRewardConditions,
    validateCurrentStep,
    setStep,

    // Display step API
    ...displayApi,
  }
}
